import { CloseRounded, InfoOutlined } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getTruckListForGroup } from 'app/entities/truck-implement/truck-implement.reducer';
import { TruckImplementListDTO } from 'app/shared/model/truck-implement.model';
import { Loading } from 'app/shared/util/loading';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface ITruckGroupTruckSelectionModalProps {
  handleSave: (selectedTrucks: number[], snapshot: TruckImplementListDTO[]) => void;
  toggleModal: () => void;
  initialSelectedTrucks: number[];
  open: boolean;
  currentGroupId?: number;
  companyId?: number;
  isDefaultGroup?: boolean;
}

export const TruckGroupTruckSelectionModal = ({
  handleSave,
  toggleModal,
  initialSelectedTrucks,
  open,
  currentGroupId,
  companyId,
  isDefaultGroup = false,
}: ITruckGroupTruckSelectionModalProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const truckList = useAppSelector(state => {
    const alt2 = state.truckImplement.entitiesAlt2;
    return Array.isArray(alt2) ? (alt2 as TruckImplementListDTO[]) : [];
  });
  const loading = useAppSelector(state => state.truckImplement.loading);

  const [selectedTrucks, setSelectedTrucks] = useState<Set<number>>(
    new Set(initialSelectedTrucks || [])
  );
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [filtering, setFiltering] = useState(false);

  const debouncedSetFilterValue = useCallback(
    debounce(value => {
      setDebouncedFilterValue(value);
      setFiltering(false);
    }, 1000),
    []
  );

  const handleFilterChange = event => {
    const { value } = event.target;
    if (value) {
      setFilterValue(value);
      setFiltering(true);
      debouncedSetFilterValue(value);
    } else {
      setFilterValue('');
      setDebouncedFilterValue('');
    }
  };

  useEffect(() => {
    dispatch(getTruckListForGroup(companyId));
  }, [companyId]);

  const sortedTrucks = useMemo(() => {
    return [...(truckList || [])].sort((a, b) => {
      const isSelectedA = initialSelectedTrucks?.includes(a.id);
      const isSelectedB = initialSelectedTrucks?.includes(b.id);
      if (isSelectedA && !isSelectedB) return -1;
      if (!isSelectedA && isSelectedB) return 1;
      return (a.licensePlate || '').localeCompare(b.licensePlate || '');
    });
  }, [truckList]);

  const filteredTrucks = useMemo(() => {
    if (!debouncedFilterValue) return sortedTrucks;
    return sortedTrucks.filter(t =>
      (t.licensePlate || '').toLowerCase().includes(debouncedFilterValue.toLowerCase())
    );
  }, [sortedTrucks, debouncedFilterValue]);

  const isFromAnotherGroup = (truck: TruckImplementListDTO) =>
    truck.truckGroupId != null &&
    (currentGroupId == null || truck.truckGroupId !== currentGroupId);

  const isLocked = (truck: TruckImplementListDTO) =>
    isDefaultGroup && initialSelectedTrucks?.includes(truck.id);

  const selectableTrucks = filteredTrucks.filter(t => !isLocked(t));
  const allFilteredSelected = selectableTrucks.length > 0 && selectableTrucks.every(t => selectedTrucks.has(t.id));
  const someFilteredSelected = selectableTrucks.some(t => selectedTrucks.has(t.id));

  const handleSelectAll = () => {
    const selectableIds = selectableTrucks.map(t => t.id);
    const allSelected = selectableIds.every(id => selectedTrucks.has(id));
    setSelectedTrucks(prev => {
      const next = new Set(prev);
      selectableIds.forEach(id => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleToggle = (id: number) => {
    if (isDefaultGroup && initialSelectedTrucks?.includes(id)) {
      return;
    }
    setSelectedTrucks(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSaveClick = () => {
    handleSave(Array.from(selectedTrucks), truckList || []);
    toggleModal();
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow: theme.shadows[24],
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            Ativos
            <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 400 }}>
              ({selectedTrucks.size}/{filteredTrucks.length} selecionados)
            </Typography>
          </Typography>
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {isDefaultGroup && (
            <Box
              display="flex"
              alignItems="flex-start"
              gap={1}
              mb={2}
              p={1.5}
              sx={{ borderRadius: '8px', backgroundColor: alpha(theme.palette.text.primary, 0.05) }}
            >
              <InfoOutlined sx={{ fontSize: 16, mt: '2px', opacity: 0.6, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">
                Ativos marcados como <strong>Grupo Geral</strong> não podem ser removidos aqui.
                Para realocá-los, selecione-os diretamente no grupo de destino.
              </Typography>
            </Box>
          )}
          {!loading && !!truckList?.length && (
            <>
              <Box display="flex" alignItems="center" mt={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  value={filterValue}
                  onChange={handleFilterChange}
                  placeholder="Buscar por placa"
                  InputProps={{
                    sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                    endAdornment: (
                      <InputAdornment position="end">
                        {!filterValue ? (
                          <SearchOutlinedIcon color="action" />
                        ) : (
                          <IconButton size="small" onClick={() => handleFilterChange({ target: { value: '' } })}>
                            <CloseOutlinedIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected && !allFilteredSelected}
                      onChange={handleSelectAll}
                      color="secondary"
                    />
                  }
                  label="Selecionar Todos"
                />
              </Box>
            </>
          )}
          <Box sx={{ mt: 1 }}>
            {filtering || loading ? (
              <Loading />
            ) : filteredTrucks.length > 0 ? (
              filteredTrucks.map(truck => (
                <Box
                  key={truck.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => handleToggle(truck.id)}
                  style={{ cursor: isLocked(truck) ? 'not-allowed' : 'pointer' }}
                  sx={{
                    py: 0.75,
                    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    '&:last-child': { borderBottom: 'none' },
                    opacity: isLocked(truck) ? 0.45 : 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox
                      checked={selectedTrucks.has(truck.id)}
                      color="secondary"
                      disabled={isLocked(truck)}
                    />
                    <Typography variant="subtitle2">{truck.licensePlate}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isLocked(truck) && (
                      <Chip
                        label="Grupo Geral"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', opacity: 0.7 }}
                      />
                    )}
                    {!isLocked(truck) && isFromAnotherGroup(truck) && selectedTrucks.has(truck.id) && (
                      <Chip
                        label={`Mover de: ${truck.truckGroupName}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    )}
                    {!isLocked(truck) && truck.truckGroupId != null && !isFromAnotherGroup(truck) && (
                      <Chip
                        label="Grupo atual"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', opacity: 0.6 }}
                      />
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Box display="flex" justifyContent="center" mt={2} mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum ativo encontrado.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClick} disableRipple variant="contained" color="secondary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
